import React, { useState, useEffect } from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import './List.less';
import SortDirection from '../../common/model/base/SortDirection';
import Pager from '../../common/model/base/Pager';
import StringUtil from '../../common/util/StringUtil';
import DateUtil from '../../common/util/DateUtil';
import FilterPanel from '../widget/filter/FilterPanel';
import TableEmpty from '../widget/TableEmpty';
import Dept, { DeptType, DeptTypeList } from '../../common/model/department/Dept';
import HwzComponent from '../../common/component/HwzComponent';
import HwzTitle from '../widget/HwzTitle';
import SafeUtil from '../../common/util/SafeUtil';
import { ColumnProps } from 'antd/lib/table';
import { 
  Button, 
  Modal, 
  Table, 
  Tag, 
  Tooltip, 
  Input,
  Space,
  Form,
  Card,
  Row,
  Col,
  Popconfirm,
  message,
  Select,
  Upload,
  Progress,
  Alert,
  Checkbox
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExclamationCircleFilled,
  SearchOutlined,
  ReloadOutlined,
  FolderOutlined,
  ExportOutlined,
  ImportOutlined,
  DownloadOutlined,
  UploadOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import MessageBoxUtil from '../../common/util/MessageBoxUtil';
import Color from '../../common/model/base/option/Color';
import Lang from '../../common/model/global/Lang';
import DeptModal from './DeptModal';

interface IProps extends RouteComponentProps {}

interface IState {
  expandedRowKeys: string[];
  selectedRowKeys: string[];
  loading: boolean;
  searchParams: {
    name?: string;
    shortName?: string;
    type?: DeptType;
    status?: string;
  };
  modalVisible: boolean;
  modalType: 'add' | 'edit' | 'view';
  currentDept?: Dept;
  treeData: any[];
  allDepts: Dept[];
  hasSearchCondition: boolean;
  importVisible: boolean;
  importData: any[];
  importProgress: number;
  importStatus: 'active' | 'exception' | 'success' | 'normal';
  importMessage: string;
  isImporting: boolean;
  importOptions: {
    updateExisting: boolean;
    keepRelations: boolean;
    skipErrors: boolean;
  };
}

export default class DepartmentList extends HwzComponent<IProps, IState> {
  // 获取分页的帮助器
  pager: Pager<Dept> = new Pager<Dept>(this, Dept, 100); // 调整为较大的分页大小以适应树形数据

  // API基础路径
  private baseUrl = '/api/sys/dept';

  // 添加搜索表单引用
  searchFormRef = React.createRef<any>();

// 导入模板结构 - 注意：CSV中使用remark，但实际映射到memo字段
private importTemplate = [
  { key: 'shortName', name: Lang.t('department.shortName'), required: true, example: '技术部' },
  { key: 'name', name: Lang.t('department.fullName'), required: true, example: '技术研发部' },
  { key: 'type', name: Lang.t('department.type'), required: true, example: 'DEPARTMENT', options: ['COMPANY', 'DEPARTMENT', 'TEAM'] },
  { key: 'parentCode', name: Lang.t('department.parentCode'), required: false, example: 'JSB001', description: Lang.t('department.parentCodeDesc') },
  { key: 'orderIndex', name: Lang.t('department.orderIndex'), required: false, example: '10', description: Lang.t('department.orderIndexDesc') },
  { key: 'status', name: Lang.t('department.status'), required: false, example: '1', options: ['1', '0'] },
  { key: 'remark', name: Lang.t('department.remark'), required: false, example: '负责技术研发工作' } // CSV中使用remark
];
  constructor(props: IProps) {
    super(props);

    this.state = {
      expandedRowKeys: [],
      selectedRowKeys: [],
      loading: false,
      searchParams: {},
      modalVisible: false,
      modalType: 'add',
      currentDept: undefined,
      treeData: [],
      allDepts: [],
      hasSearchCondition: false,
      importVisible: false,
      importData: [],
      importProgress: 0,
      importStatus: 'normal',
      importMessage: '',
      isImporting: false,
      importOptions: {
        updateExisting: true,
        keepRelations: true,
        skipErrors: true
      }
    };
  }

  componentDidMount() {
    let that = this;
    console.log('DepartmentList mounted, current path:', that.props.location.pathname);
    
    // 设置默认排序
    if (that.pager && that.pager.setFilterValue) {
      that.pager.setFilterValue('orderIndex', SortDirection.ASC);
    }
    
    that.pager.enableHistory();
    that.refresh();
  }

  // ==================== 导入导出功能开始 ====================

  exportToExcel = () => {
    const { treeData } = this.state;
    const flatData = this.flattenTreeData(treeData);
    
    if (flatData.length === 0) {
      message.warning(Lang.t('department.exportEmpty'));
      return;
    }
  
    // 准备表头 - 增加 parentCode 列
    const headers = [
      Lang.t('department.id'),
      Lang.t('department.shortName'),
      Lang.t('department.fullName'),
      Lang.t('department.type'),
      Lang.t('department.parentDept'),
      Lang.t('department.parentCode'),  // 新增：父部门代码（用于导入关联）
      Lang.t('department.parentId'),
      Lang.t('department.status'),
      Lang.t('department.orderIndex'),
      Lang.t('createTime'),
      Lang.t('updateTime'),
      Lang.t('department.remark')
    ];
  
    // 准备数据行 - 增加 parentCode 数据
    const rows = flatData.map(dept => {
      // 查找父部门
      const parentDept = flatData.find(d => d.id === dept.parentId);
      
      // 生成部门代码（用于关联） - 优先使用 shortName
      const deptCode = dept.shortName || dept.name || '';
      
      // 获取父部门代码 - 优先使用父部门的 shortName，如果没有则使用 name
      const parentCode = parentDept ? (parentDept.shortName || parentDept.name) : '';
      
      return [
        dept.id || '',
        dept.shortName || dept.name || '',
        dept.name || '',
        this.getDeptTypeText(dept.type),
        parentDept ? (parentDept.shortName || parentDept.name) : '', // 父部门名称
        parentCode, // 父部门代码（用于导入关联）
        dept.parentId || '',
        dept.status === '1' ? Lang.t('department.enabled') : Lang.t('department.disabled'),
        dept.orderIndex || '0',
        dept.createTime ? DateUtil.simpleDateTime(dept.createTime) : '',
        dept.updateTime ? DateUtil.simpleDateTime(dept.updateTime) : '',
        dept.remark || ''
      ];
    });
  
    // 创建CSV内容
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
  
    // 创建Blob并下载
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    // 使用 formatDateForFile 方法生成时间戳
    const timestamp = this.formatDateForFile(new Date(), true);
    
    link.setAttribute('download', `departments_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // 释放URL对象
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 100);
    
    message.success(Lang.t('department.exportSuccess', flatData.length.toString()));
  };
// 在组件类中添加日期格式化辅助方法
private formatDateForFile = (date: Date, includeTime: boolean = false): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  
  if (includeTime) {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${year}${month}${day}_${hours}${minutes}${seconds}`;
  }
  
  return `${year}${month}${day}`;
};
  // 导出为JSON
  exportToJson = () => {
    const { treeData } = this.state;
    
    if (treeData.length === 0) {
      message.warning(Lang.t('department.exportEmpty'));
      return;
    }
  
    // 创建导出数据（包含树形结构）
    const exportData = {
      version: '1.0',
      exportTime: new Date().toISOString(),
      total: this.flattenTreeData(treeData).length,
      departments: this.prepareExportData(treeData)
    };
  
    // 创建JSON字符串并下载
    const jsonStr = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    // 生成时间戳 - 使用我们之前创建的日期格式化方法
    const timestamp = this.formatDateForFile(new Date(), true);
    link.setAttribute('download', `departments_${timestamp}.json`);
    
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // 释放 URL 对象
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 100);
    
    message.success(Lang.t('department.exportSuccess', exportData.total.toString()));
  };

  // 准备导出数据（树形结构转扁平化，便于导入）
// 在 prepareExportData 方法中确保包含 parentCode
prepareExportData = (treeData: any[], parentCode?: string): any[] => {
  let result: any[] = [];
  
  treeData.forEach(dept => {
    // 生成部门代码（用于建立父子关系）- 优先使用 shortName
    const deptCode = dept.shortName || dept.name || '';
    
    // 获取父部门代码
    let parentCodeValue = '';
    if (dept.parentId && dept.parentId !== 0) {
      // 查找父部门
      const parentDept = this.flattenTreeData(this.state.treeData).find(d => d.id === dept.parentId);
      parentCodeValue = parentDept ? (parentDept.shortName || parentDept.name) : '';
    }
    
    const exportDept = {
      id: dept.id,
      code: deptCode,
      shortName: dept.shortName,
      name: dept.name,
      type: dept.type,
      parentCode: parentCodeValue, // 使用实际的父部门代码
      parentId: dept.parentId,
      status: dept.status || '1',
      orderIndex: dept.orderIndex || 0,
      remark: dept.remark || '',
      createTime: dept.createTime,
      updateTime: dept.updateTime
    };
    
    result.push(exportDept);
    
    // 递归处理子部门，传入当前部门的代码作为子部门的 parentCode
    if (dept.children && dept.children.length > 0) {
      const childExports = this.prepareExportData(dept.children, deptCode);
      result = result.concat(childExports);
    }
  });
  
  return result;
};

downloadTemplate = () => {
  // 创建模板数据 - 包含完整的层级关系，注意：CSV中使用remark，但实际映射到memo字段
  const templateData = [
    {
      shortName: '总部',
      name: '公司总部',
      type: 'COMPANY',
      parentCode: '', // 根部门，没有父部门
      orderIndex: '10',
      status: '1',
      remark: '公司总部'
    },
    {
      shortName: '技术部',
      name: '技术研发部',
      type: 'DEPARTMENT',
      parentCode: '总部', // 父部门是总部
      orderIndex: '20',
      status: '1',
      remark: '负责技术研发'
    },
    {
      shortName: '前端组',
      name: '前端开发组',
      type: 'TEAM',
      parentCode: '技术部', // 父部门是技术部
      orderIndex: '30',
      status: '1',
      remark: '负责前端开发'
    },
    {
      shortName: '后端组',
      name: '后端开发组',
      type: 'TEAM',
      parentCode: '技术部', // 父部门也是技术部
      orderIndex: '40',
      status: '1',
      remark: '负责后端开发'
    },
    {
      shortName: '市场部',
      name: '市场营销部',
      type: 'DEPARTMENT',
      parentCode: '总部', // 父部门是总部
      orderIndex: '50',
      status: '1',
      remark: '负责市场推广'
    },
    {
      shortName: '销售部',
      name: '销售事业部',
      type: 'DEPARTMENT',
      parentCode: '总部', // 父部门是总部
      orderIndex: '60',
      status: '1',
      remark: '负责销售工作'
    },
    {
      shortName: '客服组',
      name: '客户服务组',
      type: 'TEAM',
      parentCode: '销售部', // 父部门是销售部
      orderIndex: '70',
      status: '1',
      remark: '负责客户服务'
    }
  ];

  // 创建CSV内容
  const headers = this.importTemplate.map(col => col.name);
  const rows = templateData.map(row => {
    return this.importTemplate.map(col => {
      // 安全访问属性
      const rowAny = row as any;
      const value = rowAny[col.key] || '';
      return value;
    });
  });

  // 构建CSV字符串
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
  ].join('\n');

  // 生成日期字符串
  const now = new Date();
  const year = now.getFullYear();
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const day = now.getDate().toString().padStart(2, '0');
  const dateStr = `${year}${month}${day}`;
  
  // 创建Blob对象并下载
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `department_template_${dateStr}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // 释放 URL 对象
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 100);
  
  // 显示成功消息
  message.success(Lang.t('department.templateDownloadSuccess'));
};

  // 打开导入对话框
  openImportDialog = () => {
    this.setState({
      importVisible: true,
      importData: [],
      importProgress: 0,
      importStatus: 'normal',
      importMessage: '',
      isImporting: false
    });
  };

  // 关闭导入对话框
  closeImportDialog = () => {
    this.setState({
      importVisible: false
    });
  };

  // 处理文件上传
  handleFileUpload = (file: File) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          let parsedData: any[] = [];
          
          // 根据文件类型解析
          if (file.name.endsWith('.csv') || file.name.endsWith('.txt')) {
            parsedData = this.parseCSV(content);
          } else if (file.name.endsWith('.json')) {
            parsedData = this.parseJSON(content);
          } else {
            throw new Error(Lang.t('department.unsupportedFormat'));
          }
          
          // 验证数据
          const validationResult = this.validateImportData(parsedData);
          
          if (!validationResult.valid) {
            reject(new Error(validationResult.message));
            return;
          }
          
          this.setState({
            importData: parsedData,
            importMessage: Lang.t('department.fileReady', parsedData.length.toString())
          });
          resolve(parsedData);
          
        } catch (error: any) {
          reject(error);
        }
      };
      
      reader.onerror = () => {
        reject(new Error(Lang.t('department.fileReadError')));
      };
      
      reader.readAsText(file);
    });
  };

  // 解析CSV文件
  parseCSV = (content: string): any[] => {
    const lines = content.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error(Lang.t('department.csvFormatError'));
    }
    
    // 解析表头
    const headers = this.parseCSVLine(lines[0]);
    
    // 解析数据行
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = this.parseCSVLine(lines[i]);
      const row: any = {};
      
      headers.forEach((header, index) => {
        if (index < values.length) {
          const key = this.getColumnKey(header);
          if (key) {
            row[key] = values[index].trim();
          }
        }
      });
      
      // 跳过空行
      if (Object.keys(row).length > 0 && (row.shortName || row.name)) {
        data.push(row);
      }
    }
    
    return data;
  };

  // 解析CSV行（处理引号和逗号）
  parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];
      
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          current += '"';
          i++; // 跳过下一个引号
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current);
    return result;
  };

  // 解析JSON文件
  parseJSON = (content: string): any[] => {
    const data = JSON.parse(content);
    
    // 支持多种JSON格式
    if (Array.isArray(data)) {
      return data;
    } else if (data.departments && Array.isArray(data.departments)) {
      return data.departments;
    } else if (data.data && Array.isArray(data.data)) {
      return data.data;
    } else {
      throw new Error(Lang.t('department.jsonFormatError'));
    }
  };

  // 根据表头获取列键名
  getColumnKey = (header: string): string => {
    const headerLower = header.toLowerCase();
    
    const columnMap: Record<string, string> = {
      [Lang.t('department.shortName').toLowerCase()]: 'shortName',
      [Lang.t('department.fullName').toLowerCase()]: 'name',
      [Lang.t('department.type').toLowerCase()]: 'type',
      [Lang.t('department.parentCode').toLowerCase()]: 'parentCode',
      [Lang.t('department.orderIndex').toLowerCase()]: 'orderIndex',
      [Lang.t('department.status').toLowerCase()]: 'status',
      [Lang.t('department.remark').toLowerCase()]: 'remark',
      ['shortname']: 'shortName',
      ['name']: 'name',
      ['type']: 'type',
      ['parentcode']: 'parentCode',
      ['orderindex']: 'orderIndex',
      ['status']: 'status',
      ['remark']: 'remark'
    };
    
    return columnMap[headerLower] || headerLower;
  };

  // 验证导入数据
  validateImportData = (data: any[]): { valid: boolean; message: string } => {
    if (!Array.isArray(data) || data.length === 0) {
      return {
        valid: false,
        message: Lang.t('department.emptyData')
      };
    }
    
    // 检查必需字段
    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      const rowNum = i + 1;
      
      if (!row.shortName && !row.name) {
        return {
          valid: false,
          message: Lang.t('department.missingRequiredField', rowNum.toString(), Lang.t('department.shortName'))
        };
      }
      
      if (!row.type) {
        return {
          valid: false,
          message: Lang.t('department.missingRequiredField', rowNum.toString(), Lang.t('department.type'))
        };
      }
      
      // 验证部门类型
      const validTypes = ['COMPANY', 'DEPARTMENT', 'TEAM', '公司', '部门', '团队', '组织'];
      if (!validTypes.includes(row.type.toUpperCase())) {
        return {
          valid: false,
          message: Lang.t('department.invalidType', rowNum.toString(), row.type)
        };
      }
      
      // 验证状态
      if (row.status && !['0', '1', '启用', '停用', 'true', 'false'].includes(row.status.toString())) {
        return {
          valid: false,
          message: Lang.t('department.invalidStatus', rowNum.toString(), row.status)
        };
      }
    }
    
    // 检查循环引用
    const codeMap: Record<string, string> = {};
    data.forEach((row, index) => {
      if (row.shortName) {
        const code = row.shortName.trim();
        if (codeMap[code]) {
          throw new Error(Lang.t('department.duplicateCode', code));
        }
        codeMap[code] = row.parentCode || '';
      }
    });
    
    return {
      valid: true,
      message: Lang.t('department.dataValid', data.length.toString())
    };
  };

// 通用的HTTP保存方法
private saveDeptViaHttp = (dept: Dept, resolve: Function, reject: Function): void => {
  const url = dept.id ? '/api/sys/dept/update' : '/api/sys/dept/create';
  const method = 'POST';
  
  const formData = new FormData();
  
  // 添加所有字段到 FormData
  const addToFormData = (obj: any, prefix = '') => {
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      if (value !== undefined && value !== null) {
        if (typeof value === 'object') {
          addToFormData(value, prefix ? `${prefix}.${key}` : key);
        } else {
          formData.append(prefix ? `${prefix}.${key}` : key, value.toString());
        }
      }
    });
  };
  
// 添加部门数据
const deptData: any = {
  name: dept.name,
  shortName: dept.shortName || dept.name,
  type: dept.type,
  status: dept.status || '1',
  orderIndex: dept.orderIndex || 0,
  memo: dept.memo || '' // 改为 memo 字段
};
  
  if (dept.id) {
    deptData.id = dept.id;
  }
  
  if (dept.parentId && dept.parentId !== 0) {
    deptData.parentId = dept.parentId;
  }
  
  addToFormData(deptData);
  
  fetch(url, {
    method,
    body: formData
  })
  .then(response => response.json())
  .then(data => {
    console.log('部门保存响应:', data);
    
    // 判断是否成功
    let success = false;
    let msg = '';
    
    if (data) {
      if (data.Code === 200 || data.code === 200 || data.code === 0 || data.code === "OK") {
        success = true;
        msg = data.Msg || data.msg || data.message;
        
        // 设置返回的ID
        if (data.data && data.data.id) {
          dept.id = data.data.id;
        } else if (data.id) {
          dept.id = data.id;
        }
      }
    }
    
    if (success) {
      resolve();
    } else {
      const errorMsg = data.Msg || data.msg || data.message || Lang.t('operationFailed');
      reject(new Error(errorMsg));
    }
  })
  .catch(error => {
    console.error('保存部门HTTP请求失败:', error);
    reject(error);
  });
};

// 创建一个更稳健的导入方法，完全参考已有流程
private executeImport = async () => {
  const { importData, importOptions } = this.state;
  
  // 1. 获取现有部门数据
  const existingDepts = this.flattenTreeData(this.state.treeData);
  const deptMap = new Map<string, any>();
  
  existingDepts.forEach(dept => {
    if (dept.shortName) {
      deptMap.set(dept.shortName.trim(), dept);
    }
    if (dept.name) {
      deptMap.set(dept.name.trim(), dept);
    }
  });
  
  // 2. 准备导入数据
  const processedData = this.prepareImportData(importData, deptMap, importOptions.updateExisting);
  
  // 3. 按照层级顺序导入（先父后子）
  const sortedData = this.sortByHierarchy(processedData, deptMap);
  
  // 4. 执行导入
  const total = sortedData.length;
  let successCount = 0;
  let errorCount = 0;
  const errors: string[] = [];
  
  for (let i = 0; i < sortedData.length; i++) {
    const item = sortedData[i];
    
    try {
      // 更新进度
      const progress = Math.round(((i + 1) / total) * 100);
      this.setState({
        importProgress: progress,
        importMessage: Lang.t('department.importingDept', item.dept.shortName || item.dept.name, (i + 1).toString(), total.toString())
      });
      
      // 保存部门 - 参考新建部门的方式
      await this.saveDepartment(item.dept);
      successCount++;
      
      // 更新映射
      if (item.dept.id && item.dept.shortName) {
        deptMap.set(item.dept.shortName, item.dept);
      }
      
    } catch (error: any) {
      errorCount++;
      errors.push(`${item.dept.shortName || item.dept.name}: ${error.message}`);
      
      if (!importOptions.skipErrors) {
        throw error;
      }
    }
    
    // 避免请求过于频繁
    await this.delay(100);
  }
  
  return { successCount, errorCount, errors };
};
// 准备导入数据
private prepareImportData = (data: any[], deptMap: Map<string, any>, updateExisting: boolean): any[] => {
  return data.map(row => {
    // 创建 Dept 对象
    const dept = new Dept();
    
    // 设置基本属性
    dept.name = row.name;
    dept.shortName = row.shortName || row.name;
    dept.type = this.normalizeDeptType(row.type);
    dept.status = this.normalizeStatus(row.status);
    dept.orderIndex = parseInt(row.orderIndex) || 0;
    dept.memo = row.remark || ''; // 改为 memo 字段
    
    // 处理父部门
    if (row.parentCode && deptMap.has(row.parentCode)) {
      const parentDept = deptMap.get(row.parentCode);
      dept.parentId = parentDept.id;
    }
    
    // 如果是更新已存在的部门
    const existingDept = deptMap.get(dept.shortName) || deptMap.get(dept.name);
    if (existingDept && updateExisting) {
      dept.id = existingDept.id;
    }
    
    return {
      dept,
      parentCode: row.parentCode
    };
  });
};
// 保存部门 - 参考新建部门的实际流程
private saveDepartment = async (dept: Dept): Promise<void> => {
  return new Promise((resolve, reject) => {
    // 检查 Dept 类是否有保存方法
    if (typeof (dept as any).httpSave === 'function') {
      (dept as any).httpSave(
        (response: any) => {
          console.log('Dept.httpSave 成功:', response);
          if (response.data && response.data.id) {
            dept.id = response.data.id;
          }
          resolve();
        },
        (error: any) => {
          console.error('Dept.httpSave 失败:', error);
          reject(error);
        }
      );
    } else if (typeof (dept as any).save === 'function') {
      (dept as any).save()
        .then(() => resolve())
        .catch((error: any) => reject(error));
    } else {
      // 使用通用的 HTTP 保存
      this.saveDeptViaHttp(dept, resolve, reject);
    }
  });
};
// 延迟函数
private delay = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};
// 部门类型标准化
private normalizeDeptType = (type: any): DeptType => {
  if (!type) return DeptType.DEPARTMENT;
  
  const typeStr = String(type).toUpperCase();
  switch (typeStr) {
    case 'COMPANY':
    case '公司':
      return DeptType.COMPANY;
    case 'DEPARTMENT':
    case '部门':
      return DeptType.DEPARTMENT;
    case 'TEAM':
    case '团队':
      return DeptType.TEAM;
    default:
      return DeptType.DEPARTMENT;
  }
};
// 状态标准化
private normalizeStatus = (status: any): string => {
  if (!status) return '1';
  
  const statusStr = String(status);
  if (statusStr === '0' || statusStr === 'false' || statusStr === '停用') {
    return '0';
  }
  return '1';
};
// 按层级排序
private sortByHierarchy = (data: any[], deptMap: Map<string, any>): any[] => {
  // 第一轮：没有父部门或父部门已存在的
  const level1 = data.filter(item => !item.parentCode || deptMap.has(item.parentCode));
  
  // 第二轮：有父部门但父部门在本次导入中的
  const level2 = data.filter(item => item.parentCode && !deptMap.has(item.parentCode));
  
  // 递归处理层级
  const result: any[] = [];
  const processed = new Set<string>();
  
  const processItem = (item: any) => {
    // 如果已经处理过，跳过
    const key = item.dept.shortName || item.dept.name;
    if (processed.has(key)) {
      return;
    }
    
    // 如果有父部门且父部门还未处理
    if (item.parentCode && !processed.has(item.parentCode)) {
      // 查找父部门是否在本次导入中
      const parentItem = data.find(d => (d.dept.shortName === item.parentCode || d.dept.name === item.parentCode));
      if (parentItem) {
        processItem(parentItem);
      }
    }
    
    // 处理当前项
    result.push(item);
    processed.add(key);
  };
  
  // 先处理第一轮
  level1.forEach(processItem);
  
  // 再处理第二轮
  level2.forEach(processItem);
  
  return result;
};
// 转换部门类型
private convertDeptType = (type: string): string => {
  const typeStr = type.toString().toUpperCase();
  switch (typeStr) {
    case 'COMPANY':
    case '公司':
      return DeptType.COMPANY;
    case 'DEPARTMENT':
    case '部门':
      return DeptType.DEPARTMENT;
    case 'TEAM':
    case '团队':
      return DeptType.TEAM;
    default:
      return DeptType.DEPARTMENT;
  }
};
// 这是 startImport 方法的完整实现，包含了 processImportData 的调用
startImport = async () => {
  const { importData, importOptions } = this.state;
  
  if (importData.length === 0) {
    message.warning(Lang.t('department.noImportData'));
    return;
  }

  this.setState({
    isImporting: true,
    importProgress: 0,
    importStatus: 'active',
    importMessage: Lang.t('department.startingImport')
  });

  try {
    // 获取现有部门数据
    const existingDepts = this.flattenTreeData(this.state.treeData);
    const nameToDeptMap: Record<string, any> = {};
    
    existingDepts.forEach(dept => {
      if (dept.shortName) {
        nameToDeptMap[dept.shortName.trim()] = dept;
      }
      if (dept.name) {
        nameToDeptMap[dept.name.trim()] = dept;
      }
    });

    // 处理导入数据
    const processedData = this.processImportData(importData);
    
    // 准备部门对象数组，并添加 parentCode 信息
    const deptList: any[] = [];
    for (const deptData of processedData) {
      // 创建部门对象
      const dept = {
        name: deptData.name,
        shortName: deptData.shortName || deptData.name,
        type: this.convertDeptType(deptData.type),
        status: this.convertStatus(deptData.status),
        orderIndex: parseInt(deptData.orderIndex) || 0,
        memo: deptData.memo || '',
        parentCode: deptData.parentCode || '' // 保留 parentCode 用于后续查找
      };
      
      // 如果部门已存在，设置ID以便更新
      const existingDept = nameToDeptMap[deptData.shortName || deptData.name];
      if (existingDept && importOptions.updateExisting) {
        (dept as any).id = existingDept.id;
      }
      
      deptList.push({
        dept,
        deptData,
        key: deptData.shortName || deptData.name
      });
    }

    // ========== 关键修复：拓扑排序，确保父部门先于子部门导入 ==========
    const sortedDeptList = this.topologicalSort(deptList);
    
    if (!sortedDeptList) {
      throw new Error(Lang.t('department.circularDependency'));
    }

    // 分步导入
    const total = sortedDeptList.length;
    let successCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    for (let i = 0; i < sortedDeptList.length; i++) {
      const { dept, deptData } = sortedDeptList[i];
      
      try {
        // 更新进度
        const progress = Math.round(((i + 1) / total) * 100);
        this.setState({
          importProgress: progress,
          importMessage: Lang.t('department.importingDept', dept.shortName || dept.name, (i + 1).toString(), total.toString())
        });

        // ========== 关键修复：在导入前查找父部门ID ==========
        let parentId = 0;
        if (deptData.parentCode) {
          // 首先在已存在的部门中查找
          if (nameToDeptMap[deptData.parentCode]) {
            parentId = nameToDeptMap[deptData.parentCode].id;
          } else {
            // 在本次导入的部门中查找（已导入的）
            const parentDeptInList = sortedDeptList
              .slice(0, i) // 只查找已导入的部门
              .find(item => item.dept.shortName === deptData.parentCode || 
                           item.dept.name === deptData.parentCode);
            if (parentDeptInList && parentDeptInList.dept.id) {
              parentId = parentDeptInList.dept.id;
            }
          }
        }
        
        // 设置父部门ID
        (dept as any).parentId = parentId;

        // 保存部门
        await this.saveDept(dept, importOptions.updateExisting);

        successCount++;
        
        // 更新映射 - 非常重要！更新已导入的部门信息
        if ((dept as any).id) {
          // 添加当前部门到映射中
          nameToDeptMap[dept.shortName || dept.name] = {
            ...dept,
            id: (dept as any).id
          };
          
          // 如果部门名称不同，也添加到映射
          if (dept.name && dept.name !== dept.shortName) {
            nameToDeptMap[dept.name] = {
              ...dept,
              id: (dept as any).id
            };
          }
        }
        
      } catch (error: any) {
        errorCount++;
        const errorMsg = error.message || Lang.t('operationFailed');
        errors.push(Lang.t('department.importError', dept.shortName || dept.name, errorMsg));
        console.error(`导入部门 ${dept.shortName || dept.name} 失败:`, error);
        
        if (!importOptions.skipErrors) {
          throw error;
        }
      }
      
      // 短暂延迟，避免请求过于频繁
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // 导入完成
    this.setState({
      importProgress: 100,
      importStatus: successCount > 0 ? 'success' : 'exception',
      importMessage: Lang.t('department.importComplete', successCount.toString(), errorCount.toString())
    });

    // 显示导入结果
    if (errorCount > 0) {
      message.warning(Lang.t('department.importWithErrors', successCount.toString(), errorCount.toString()));
    } else {
      message.success(Lang.t('department.importSuccess', successCount.toString()));
    }

    // 刷新列表
    setTimeout(() => {
      this.refresh();
      this.closeImportDialog();
    }, 1500);

  } catch (error: any) {
    console.error('导入失败:', error);
    this.setState({
      importStatus: 'exception',
      importMessage: Lang.t('department.importFailed') + ': ' + (error.message || Lang.t('operationFailed'))
    });
    message.error(Lang.t('department.importFailed'));
  } finally {
    this.setState({ isImporting: false });
  }
};
// 新增：拓扑排序方法，确保父部门在子部门之前导入
private topologicalSort = (deptList: any[]): any[] | null => {
  try {
    // 构建部门名称到索引的映射
    const nameToIndex: Record<string, number> = {};
    deptList.forEach((item, index) => {
      const key = item.key;
      if (key) {
        nameToIndex[key] = index;
      }
    });

    // 构建邻接表和入度表
    const adjList: number[][] = new Array(deptList.length).fill(0).map(() => []);
    const inDegree: number[] = new Array(deptList.length).fill(0);

    deptList.forEach((item, index) => {
      const parentCode = item.deptData.parentCode;
      if (parentCode && nameToIndex[parentCode] !== undefined) {
        const parentIndex = nameToIndex[parentCode];
        adjList[parentIndex].push(index);
        inDegree[index]++;
      }
    });

    // 找到所有入度为0的节点（没有父部门的节点）
    const queue: number[] = [];
    inDegree.forEach((degree, index) => {
      if (degree === 0) {
        queue.push(index);
      }
    });

    const result: number[] = [];
    while (queue.length > 0) {
      const current = queue.shift()!;
      result.push(current);
      
      // 减少相邻节点的入度
      adjList[current].forEach(neighbor => {
        inDegree[neighbor]--;
        if (inDegree[neighbor] === 0) {
          queue.push(neighbor);
        }
      });
    }

    // 检查是否有环（循环依赖）
    if (result.length !== deptList.length) {
      console.error('发现循环依赖，无法进行拓扑排序');
      return null;
    }

    // 返回排序后的部门列表
    return result.map(index => deptList[index]);
  } catch (error) {
    console.error('拓扑排序失败:', error);
    return null;
  }
};
// 转换状态
private convertStatus = (status: string): string => {
  if (!status) return '1';
  
  const statusStr = status.toString();
  if (statusStr === '0' || statusStr === 'false' || statusStr === '停用') {
    return '0';
  }
  return '1';
};

// 修改为 private 方法（如果需要的话）
// 在 processImportData 方法中，修改 remark 为 memo
private processImportData = (data: any[]): any[] => {
  return data.map(row => {
    // 转换部门类型
    let type = row.type;
    if (typeof type === 'string') {
      type = type.toUpperCase();
      if (type === '公司' || type === '组织') type = 'COMPANY';  // 增加“组织”
      if (type === '部门') type = 'DEPARTMENT';
      if (type === '团队') type = 'TEAM';
    }

    // 转换状态
    let status = row.status;
    if (status === '启用' || status === 'true') status = '1';
    if (status === '停用' || status === 'false') status = '0';
    if (status === undefined || status === null) status = '1';

    return {
      ...row,
      type: type,
      status: status.toString(),
      shortName: row.shortName || row.name,
      name: row.name,
      memo: row.remark || '' // 将 remark 映射到 memo
    };
  });
};

private saveDept = async (deptData: any, updateExisting: boolean): Promise<void> => {
  return new Promise((resolve, reject) => {
    // 创建 Dept 实例
    const dept = new Dept();
    
    // 设置部门属性
    dept.assign({
      name: deptData.name,
      shortName: deptData.shortName || deptData.name,
      type: deptData.type,
      status: deptData.status || '1',
      orderIndex: deptData.orderIndex || 0,
      memo: deptData.memo || '',
      parentId: deptData.parentId || 0 // 确保 parentId 被正确设置
    });
    
    // 如果是更新已存在的部门，设置ID
    if (deptData.id && updateExisting) {
      dept.id = deptData.id;
    }
    
    // 使用 Dept 类的 httpSave 方法
    dept.httpSave(
      (response: any) => {
        console.log('保存部门成功:', response);
        
        // 获取返回的ID
        if (response && response.data) {
          const responseData = response.data.data || response.data;
          if (responseData && responseData.id) {
            deptData.id = responseData.id; // 更新部门ID
          }
        }
        
        resolve();
      },
      (error: any) => {
        console.error('保存部门失败:', error);
        reject(error);
      }
    );
  });
};

  // 创建部门
  createDept = async (dept: Dept): Promise<void> => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      Object.keys(dept).forEach(key => {
        const value = (dept as any)[key];
        if (value !== undefined && value !== null) {
          formData.append(key, value.toString());
        }
      });

      fetch('/api/sys/dept/create', {
        method: 'POST',
        body: formData
      })
      .then(response => response.json())
      .then(data => {
        if (data && (data.Code === 200 || data.code === 200 || data.code === 0)) {
          if (data.data && data.data.id) {
            dept.id = data.data.id;
          }
          resolve();
        } else {
          reject(new Error(data.Msg || data.msg || Lang.t('operationFailed')));
        }
      })
      .catch(error => reject(error));
    });
  };

  // 更新部门
  updateDept = async (dept: Dept): Promise<void> => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      Object.keys(dept).forEach(key => {
        const value = (dept as any)[key];
        if (value !== undefined && value !== null) {
          formData.append(key, value.toString());
        }
      });

      fetch('/api/sys/dept/update', {
        method: 'POST',
        body: formData
      })
      .then(response => response.json())
      .then(data => {
        if (data && (data.Code === 200 || data.code === 200 || data.code === 0)) {
          resolve();
        } else {
          reject(new Error(data.Msg || data.msg || Lang.t('operationFailed')));
        }
      })
      .catch(error => reject(error));
    });
  };

  // ==================== 导入导出功能结束 ====================

  // 搜索方法
  handleSearch = () => {
    let that = this;
    const values = that.state.searchParams;
    
    // 检查是否有搜索条件
    const hasCondition = !!(values.name || values.shortName || values.type || values.status);
    
    // 重置分页
    that.pager.page = 0;
    
    // 重置所有过滤器
    if (that.pager.resetFilter) {
      that.pager.resetFilter();
    }
    
    // 设置查询条件
    if (values.name) {
      that.pager.setFilterValue('name', values.name);
    }
    
    if (values.shortName) {
      that.pager.setFilterValue('shortName', values.shortName);
    }
    
    if (values.type) {
      that.pager.setFilterValue('type', values.type);
    }
    
    if (values.status) {
      that.pager.setFilterValue('status', values.status);
    }
    
    // 设置默认排序
    that.pager.setFilterValue('orderIndex', SortDirection.ASC);
    
    that.setState({ hasSearchCondition: hasCondition }, () => {
      that.refresh();
    });
  };

  // 重置搜索
  handleReset = () => {
    let that = this;
    that.setState({
      searchParams: {},
      hasSearchCondition: false
    }, () => {
      if (that.pager.resetFilter) {
        that.pager.resetFilter();
      }
      that.pager.setFilterValue('orderIndex', SortDirection.ASC);
      that.refresh();
    });
  };

  convertDataFromBackend = (rawData: any): any[] => {
    console.log('=== convertDataFromBackend 开始 ===');
    console.log('输入 rawData:', rawData);
    
    if (!rawData) {
      console.log('rawData 为空，返回空数组');
      return [];
    }
    
    // 使用 Dept 类的静态方法转换数据
    try {
      const depts = Dept.convertFromBackendData(rawData);
      console.log('转换后的Dept实例数:', depts.length);
      
      // 构建树形结构
      const treeData = Dept.buildTree(depts);
      
      // 转换为前端需要的格式
      const convertToFrontendFormat = (deptList: Dept[]): any[] => {
        return deptList.map(dept => {
          const node: any = {
            id: dept.id,
            name: dept.name,
            shortName: dept.shortName,
            type: dept.type,
            parentId: dept.parentId,
            orderIndex: dept.orderIndex,
            status: dept.status,
            memo: dept.memo,
            createTime: dept.createTime,
            updateTime: dept.updateTime,
            createUser: dept.createUser,
            updateUser: dept.updateUser,
            parentName: dept.parentName,
            level: dept.level,
            key: dept.id ? dept.id.toString() : '',
            sort: dept.sort
          };
          
          // 如果有子部门，递归处理
          if (dept.children && dept.children.length > 0) {
            node.children = convertToFrontendFormat(dept.children);
          }
          
          return node;
        });
      };
      
      const result = convertToFrontendFormat(treeData);
      console.log('最终树形数据:', result);
      console.log('=== convertDataFromBackend 结束 ===');
      
      return result;
    } catch (error) {
      console.error('转换部门数据失败:', error);
      return [];
    }
  };

  refresh = () => {
    let that = this;
    
    console.log('开始刷新部门列表...');
    console.log('当前搜索条件:', that.state.searchParams);
    console.log('是否有搜索条件:', that.state.hasSearchCondition);
    
    that.setState({ loading: true }, () => {
      console.log('调用 pager.httpList...');
      
      that.pager.httpList(
        (response: any) => {
          console.log('========== 部门列表响应调试开始 ==========');
          console.log('原始响应:', response);
          console.log('响应类型:', typeof response);
          
          // 1. 提取数据数组
          const rawData = response.data || response;
          console.log('提取的 rawData:', rawData);
          
          // 2. 转换数据，提取数组（保持树形结构）
          const dataArray = that.convertDataFromBackend(rawData);
          console.log('转换后的数据数组（树形结构）:', dataArray);
          console.log('数据数组长度:', dataArray.length);
          
          // 3. 如果是树形结构，默认展开第一级
          let expandedKeys: string[] = [];
          if (dataArray.length > 0 && !that.state.hasSearchCondition) {
            // 获取所有一级节点的key
            dataArray.forEach((item: any) => {
              if (item.id) {
                expandedKeys.push(item.id.toString());
              }
            });
          }
          
          that.setState({ 
            loading: false,
            treeData: dataArray,
            expandedRowKeys: expandedKeys
          });
          
          console.log('树形数据已设置，长度:', dataArray.length);
          console.log('默认展开的节点:', expandedKeys);
          console.log('========== 部门列表响应调试结束 ==========');
        },
        (error: any) => {
          console.log('pager.httpList 失败:', error);
          that.setState({ loading: false });
        }
      );
    });
  };

  // 获取所有节点（平铺），用于统计和查找
  flattenTreeData = (treeData: any[]): any[] => {
    const result: any[] = [];
    
    const traverse = (nodes: any[], level: number = 1) => {
      nodes.forEach(node => {
        const nodeCopy = { ...node, level };
        result.push(nodeCopy);
        
        if (node.children && Array.isArray(node.children) && node.children.length > 0) {
          traverse(node.children, level + 1);
        }
      });
    };
    
    traverse(treeData);
    return result;
  };

  // 删除部门
  deleteDept = (dept: any) => {
    let that = this;
    Modal.confirm({
      title: Lang.t('department.deleteHint', dept.name),
      icon: <ExclamationCircleFilled twoToneColor={Color.WARNING} />,
      content: Lang.t('department.deleteConfirm'),
      onOk: () => {
        that.setState({ loading: true });
        
        // 直接调用删除 API
        const formData = new FormData();
        formData.append('id', dept.id ? dept.id.toString() : '');
        
        fetch('/api/sys/dept/delete', {
          method: 'POST',
          body: formData
        })
        .then(response => response.json())
        .then(data => {
          console.log('删除响应:', data);
          
          // 判断是否成功
          let success = false;
          let msg = '';
          
          if (data) {
            if (data.Code === 200 || data.code === 200 || data.code === 0 || data.code === "OK") {
              success = true;
              msg = data.Msg || data.msg || data.message;
            }
          }
          
          if (success) {
            message.success(msg || Lang.t('operationSuccess'));
            that.refresh();
          } else {
            const errorMsg = data.Msg || data.msg || data.message || Lang.t('operationFailed');
            message.error(errorMsg);
          }
        })
        .catch(error => {
          console.error('删除失败:', error);
          message.error(Lang.t('operationFailed'));
        })
        .finally(() => {
          that.setState({ loading: false });
        });
      },
    });
  };

  // 批量删除
  handleBatchDelete = () => {
    let that = this;
    const { selectedRowKeys } = that.state;
    
    if (selectedRowKeys.length === 0) {
      message.warning(Lang.t('department.selectDelete'));
      return;
    }

    Modal.confirm({
      title: Lang.t('department.batchDelete'),
      icon: <ExclamationCircleFilled twoToneColor={Color.WARNING} />,
      content: Lang.t('department.batchDeleteConfirm', selectedRowKeys.length.toString()),
      onOk: () => {
        that.setState({ loading: true });
        
        // 调用批量删除 API
        fetch('/api/sys/dept/batchDelete', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ids: selectedRowKeys })
        })
        .then(response => response.json())
        .then(data => {
          console.log('批量删除响应:', data);
          
          // 判断是否成功
          let success = false;
          
          if (data) {
            if (data.Code === 200 || data.code === 200 || data.code === 0 || data.code === "OK") {
              success = true;
            }
          }
          
          if (success) {
            message.success(Lang.t('operationSuccess'));
            that.setState({
              selectedRowKeys: []
            });
            that.refresh();
          } else {
            const errorMsg = data.Msg || data.msg || data.message || Lang.t('operationFailed');
            message.error(errorMsg);
          }
        })
        .catch(error => {
          console.error('批量删除失败:', error);
          message.error(Lang.t('operationFailed'));
        })
        .finally(() => {
          that.setState({ loading: false });
        });
      },
    });
  };

  // 切换展开/收起
  toggleExpand = (expanded: boolean, record: any) => {
    const { expandedRowKeys } = this.state;
    const key = record.id ? record.id.toString() : '';
    if (!key) return;
    
    if (expanded) {
      this.setState({
        expandedRowKeys: [...expandedRowKeys, key],
      });
    } else {
      this.setState({
        expandedRowKeys: expandedRowKeys.filter(k => k !== key),
      });
    }
  };

  // 展开所有
  expandAll = () => {
    const { treeData } = this.state;
    const allKeys = this.getAllKeys(treeData);
    this.setState({ expandedRowKeys: allKeys });
  };

  // 收起所有
  collapseAll = () => {
    this.setState({ expandedRowKeys: [] });
  };

  // 获取所有节点key
  getAllKeys = (data: any[]): string[] => {
    let keys: string[] = [];
    data.forEach(item => {
      const key = item.id ? item.id.toString() : '';
      if (key) {
        keys.push(key);
        if (item.children && item.children.length > 0) {
          keys = keys.concat(this.getAllKeys(item.children));
        }
      }
    });
    return keys;
  };

  // 获取部门类型标签颜色
  getDeptTypeColor = (type: DeptType): string => {
    switch (type) {
      case DeptType.COMPANY:
        return 'blue';
      case DeptType.DEPARTMENT:
        return 'green';
      case DeptType.TEAM:
        return 'orange';
      default:
        return 'default';
    }
  };

  // 获取部门类型显示文本
  getDeptTypeText = (type: DeptType): string => {
    const typeItem = DeptTypeList.find(item => item.value === type);
    return typeItem ? typeItem.label : type;
  };

  // 获取状态显示文本
  getStatusText = (status: string): string => {
    return status === '1' ? Lang.t('department.enabled') : Lang.t('department.disabled');
  };

  // 获取状态标签颜色
  getStatusColor = (status: string): string => {
    return status === '1' ? 'green' : 'red';
  };

  // 打开新增弹窗
  openAddModal = () => {
    const newDept = new Dept();
    newDept.status = '1'; // 默认启用
    newDept.type = DeptType.DEPARTMENT; // 默认部门类型
    
    this.setState({
      modalVisible: true,
      modalType: 'add',
      currentDept: newDept
    });
  };

  // 打开编辑弹窗
  openEditModal = (dept: any) => {
    const tempDept = new Dept();
    
    // 调用 assign 方法，让 Dept 类自己处理字段映射
    tempDept.assign(dept);
    
    this.setState({
      modalVisible: true,
      modalType: 'edit',
      currentDept: tempDept
    });
  };

  // 打开查看弹窗
  openViewModal = (dept: any) => {
    const tempDept = new Dept();
    
    // 调用 assign 方法，让 Dept 类自己处理字段映射
    tempDept.assign(dept);
    
    this.setState({
      modalVisible: true,
      modalType: 'view',
      currentDept: tempDept
    });
  };

  // 关闭弹窗
  closeModal = () => {
    this.setState({
      modalVisible: false,
      currentDept: undefined
    });
  };

  // 处理弹窗保存成功
  handleModalSuccess = () => {
    this.closeModal();
    this.refresh();
  };

  // 表格选择变化
  handleSelectChange = (selectedRowKeys: React.Key[], selectedRows: any[]) => {
    // 提取 id
    const ids = selectedRows.map(row => row.id ? row.id.toString() : '').filter(Boolean);
    this.setState({ 
      selectedRowKeys: ids
    });
  };

  // 渲染部门简称单元格 - 支持树形缩进
  renderShortNameCell = (text: any, record: any) => {
    // 计算缩进
    const level = record.level || 1;
    const indent = (level - 1) * 20;
    
    // 检查是否有子节点
    const hasChildren = record.children && record.children.length > 0;
    const isExpanded = this.state.expandedRowKeys.includes(record.id ? record.id.toString() : '');
    
    return (
      <div style={{ paddingLeft: indent, display: 'flex', alignItems: 'center' }}>
        {hasChildren && (
          <span style={{ marginRight: 8, fontSize: '12px', color: '#999', width: 16 }}>
            {isExpanded ? '▼' : '▶'}
          </span>
        )}
        {!hasChildren && (
          <span style={{ marginRight: 8, fontSize: '12px', color: '#999', width: 16 }}>•</span>
        )}
        <span 
          style={{ cursor: 'pointer', color: '#1890ff' }}
          onClick={() => this.openViewModal(record)}
        >
          {record.shortName || record.name}
        </span>
        {record.name && record.name !== record.shortName && (
          <div className="text-muted small" style={{ fontSize: '12px', marginLeft: 8 }}>
            ({record.name})
          </div>
        )}
      </div>
    );
  };

  // 渲染操作单元格
  renderActionCell = (text: any, record: any) => {
    return (
      <Space size="small" className="department-actions">
        {/* 查看 */}
        <Tooltip title={Lang.t('view')}>
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            style={{ color: '#1890ff' }}
            onClick={() => this.openViewModal(record)}
          />
        </Tooltip>

        {/* 编辑 */}
        <Tooltip title={Lang.t('edit')}>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            style={{ color: '#52c41a' }}
            onClick={() => this.openEditModal(record)}
          />
        </Tooltip>

        {/* 删除 */}
        <Tooltip title={Lang.t('delete')}>
          <Popconfirm
            title={Lang.t('department.deleteConfirm')}
            onConfirm={() => this.deleteDept(record)}
            okText={Lang.t('confirm')}
            cancelText={Lang.t('cancel')}
            okButtonProps={{ danger: true }}
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Tooltip>
      </Space>
    );
  };

  // 渲染搜索表单
  renderSearchForm = () => {
    const { searchParams } = this.state;
    
    return (
      <Card 
        title={Lang.t('search')}
        size="small"
        style={{ marginBottom: 16 }}
        className="search-panel"
      >
        <Form
          layout="inline"
          initialValues={searchParams}
          className="search-form"
        >
          <Row gutter={16} style={{ width: '100%' }}>
            <Col xs={24} sm={12} md={8} lg={6} xl={6}>
              <Form.Item
                name="name"
                label={Lang.t('department.fullName')}
                className="search-form-item"
              >
                <Input 
                  placeholder={Lang.t('department.fullNamePlaceholder')}
                  allowClear
                  value={searchParams.name}
                  onChange={(e) => this.setState(prev => ({
                    searchParams: { ...prev.searchParams, name: e.target.value }
                  }))}
                  onPressEnter={this.handleSearch}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6} xl={6}>
              <Form.Item
                name="shortName"
                label={Lang.t('department.shortName')}
                className="search-form-item"
              >
                <Input 
                  placeholder={Lang.t('department.shortNamePlaceholder')}
                  allowClear
                  value={searchParams.shortName}
                  onChange={(e) => this.setState(prev => ({
                    searchParams: { ...prev.searchParams, shortName: e.target.value }
                  }))}
                  onPressEnter={this.handleSearch}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6} xl={6}>
              <Form.Item
                name="type"
                label={Lang.t('department.type')}
                className="search-form-item"
              >
                <Select
                  placeholder={Lang.t('department.selectType')}
                  allowClear
                  style={{ width: '100%' }}
                  value={searchParams.type}
                  onChange={(value) => this.setState(prev => ({
                    searchParams: { ...prev.searchParams, type: value }
                  }))}
                >
                  {DeptTypeList.map((typeItem: any) => (
                    <Select.Option key={typeItem.value} value={typeItem.value}>
                      {typeItem.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6} xl={6}>
              <Form.Item
                name="status"
                label={Lang.t('department.status')}
                className="search-form-item"
              >
                <Select
                  placeholder={Lang.t('department.selectStatus')}
                  allowClear
                  style={{ width: '100%' }}
                  value={searchParams.status}
                  onChange={(value) => this.setState(prev => ({
                    searchParams: { ...prev.searchParams, status: value }
                  }))}
                >
                  <Select.Option value="1">{Lang.t('department.enabled')}</Select.Option>
                  <Select.Option value="0">{Lang.t('department.disabled')}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            
            <Col xs={24} style={{ textAlign: 'right', marginTop: 16 }}>
              <Space>
                <Button 
                  type="primary" 
                  icon={<SearchOutlined />}
                  onClick={this.handleSearch}
                  className="search-button"
                >
                  {Lang.t('search')}
                </Button>
                <Button 
                  onClick={this.handleReset} 
                  icon={<ReloadOutlined />}
                  className="reset-button"
                >
                  {Lang.t('reset')}
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>
    );
  };

  // 渲染导入导出工具按钮
  renderImportExportButtons = () => {
    return (
      <Space size="middle" className="import-export-buttons">
        {/* 导出下拉菜单 */}
        <Button.Group>
          <Button 
            icon={<ExportOutlined />}
            onClick={this.exportToExcel}
            className="export-button"
          >
            {Lang.t('department.exportExcel')}
          </Button>
          <Button 
            icon={<DownloadOutlined />}
            onClick={this.exportToJson}
            className="export-json-button"
          >
            JSON
          </Button>
        </Button.Group>

        {/* 导入按钮 */}
        <Button 
          type="primary"
          icon={<ImportOutlined />}
          onClick={this.openImportDialog}
          className="import-button"
        >
          {Lang.t('department.import')}
        </Button>

        {/* 模板下载 */}
        <Button 
          icon={<FileExcelOutlined />}
          onClick={this.downloadTemplate}
          className="template-button"
        >
          {Lang.t('department.downloadTemplate')}
        </Button>
      </Space>
    );
  };

  // 渲染导入对话框
  renderImportDialog = () => {
    const { 
      importVisible, 
      importProgress, 
      importStatus, 
      importMessage, 
      isImporting,
      importOptions,
      importData 
    } = this.state;

    return (
      <Modal
        title={Lang.t('department.importTitle')}
        visible={importVisible} // 👈 改这里
        onCancel={this.closeImportDialog}
        footer={[
          <Button key="cancel" onClick={this.closeImportDialog} disabled={isImporting}>
            {Lang.t('cancel')}
          </Button>,
          <Button 
            key="import" 
            type="primary" 
            onClick={this.startImport}
            loading={isImporting}
            disabled={importData.length === 0 || isImporting}
          >
            {isImporting ? Lang.t('department.importing') : Lang.t('department.startImport')}
          </Button>
        ]}
        width={700}
      >
        <div style={{ marginBottom: 16 }}>
          <Alert
            message={Lang.t('department.importTips')}
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            style={{ marginBottom: 16 }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <h4>{Lang.t('department.uploadFile')}</h4>
          <Upload
            accept=".csv,.json,.txt"
            showUploadList={true}
            beforeUpload={(file) => {
              this.handleFileUpload(file)
                .then(() => {
                  message.success(Lang.t('department.fileUploadSuccess'));
                })
                .catch((error) => {
                  message.error(error.message);
                });
              return false; // 阻止自动上传
            }}
            disabled={isImporting}
          >
            <Button icon={<UploadOutlined />} disabled={isImporting}>
              {Lang.t('department.selectFile')}
            </Button>
          </Upload>
          <div style={{ marginTop: 8, fontSize: '12px', color: '#999' }}>
            {Lang.t('department.supportedFormats')}
          </div>
        </div>

        {importData.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Alert
              message={Lang.t('department.readyToImport', importData.length.toString())}
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <h4>{Lang.t('department.importOptions')}</h4>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Checkbox
                checked={importOptions.updateExisting}
                onChange={(e) => this.setState(prev => ({
                  importOptions: { ...prev.importOptions, updateExisting: e.target.checked }
                }))}
                disabled={isImporting}
              >
                {Lang.t('department.updateExisting')}
              </Checkbox>
              
              <Checkbox
                checked={importOptions.keepRelations}
                onChange={(e) => this.setState(prev => ({
                  importOptions: { ...prev.importOptions, keepRelations: e.target.checked }
                }))}
                disabled={isImporting}
              >
                {Lang.t('department.keepRelations')}
              </Checkbox>
              
              <Checkbox
                checked={importOptions.skipErrors}
                onChange={(e) => this.setState(prev => ({
                  importOptions: { ...prev.importOptions, skipErrors: e.target.checked }
                }))}
                disabled={isImporting}
              >
                {Lang.t('department.skipErrors')}
              </Checkbox>
            </Space>
          </div>
        )}

        {isImporting && (
          <div style={{ marginTop: 16 }}>
            <h4>{Lang.t('department.importProgress')}</h4>
            <Progress 
              percent={importProgress} 
              status={importStatus}
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
            />
            <div style={{ marginTop: 8, textAlign: 'center', color: '#666' }}>
              {importMessage}
            </div>
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <h4>{Lang.t('department.templateFormat')}</h4>
          <Table
            dataSource={this.importTemplate}
            columns={[
              {
                title: Lang.t('department.fieldName'),
                dataIndex: 'name',
                width: 150,
              },
              {
                title: Lang.t('department.fieldKey'),
                dataIndex: 'key',
                width: 120,
              },
              {
                title: Lang.t('department.required'),
                dataIndex: 'required',
                width: 80,
                render: (required) => required ? '✓' : '',
              },
              {
                title: Lang.t('department.example'),
                dataIndex: 'example',
              },
              {
                title: Lang.t('department.description'),
                dataIndex: 'description',
              }
            ]}
            size="small"
            pagination={false}
            rowKey="key"
          />
        </div>
      </Modal>
    );
  };

  // 渲染表格工具栏
  renderTableToolbar = () => {
    const { selectedRowKeys } = this.state;
    const flatData = this.flattenTreeData(this.state.treeData);
    
    return (
      <div style={{ marginBottom: 16 }} className="table-toolbar">
        <Space>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={this.openAddModal}
            className="add-button"
          >
            {Lang.t('department.createDept')}
          </Button>
          
          <Button 
            icon={<ReloadOutlined />}
            onClick={this.refresh}
            className="refresh-button"
          >
            {Lang.t('refresh')}
          </Button>
          
          <Button 
            icon={<FolderOutlined />}
            onClick={this.expandAll}
            className="expand-button"
          >
            {Lang.t('department.expandAll')}
          </Button>
          
          <Button 
            icon={<FolderOutlined />}
            onClick={this.collapseAll}
            className="collapse-button"
          >
            {Lang.t('department.collapseAll')}
          </Button>
          
          {selectedRowKeys.length > 0 && (
            <Popconfirm
              title={Lang.t('department.batchDeleteConfirm', selectedRowKeys.length.toString())}
              onConfirm={this.handleBatchDelete}
              okText={Lang.t('confirm')}
              cancelText={Lang.t('cancel')}
              okButtonProps={{ danger: true }}
            >
              <Button 
                type="primary" 
                danger
                icon={<DeleteOutlined />}
                className="batch-delete-button"
              >
                {Lang.t('department.batchDelete')} ({selectedRowKeys.length})
              </Button>
            </Popconfirm>
          )}
          
          {/* 导入导出按钮组 */}
          <div style={{ marginLeft: 'auto' }}>
            {this.renderImportExportButtons()}
          </div>
          
          <div style={{ color: '#666', fontSize: '14px', marginLeft: 16 }}>
            {Lang.t('plugin.total')}: {flatData.length} {Lang.t('plugin.items')}
          </div>
        </Space>
      </div>
    );
  };

  render() {
    let that = this;
    let pager = this.pager;
    const { 
      loading, 
      expandedRowKeys, 
      selectedRowKeys, 
      modalVisible, 
      modalType, 
      currentDept,
      treeData,
      hasSearchCondition
    } = this.state;

    // 获取平铺数据用于显示总数
    const flatData = this.flattenTreeData(treeData);

    const columns: ColumnProps<any>[] = [
      {
        title: Lang.t('department.id'),
        dataIndex: 'id',
        width: 80,
        render: (text: any) => (
          <span className="dept-id-cell">{text}</span>
        ),
      },
      {
        title: Lang.t('department.shortName'),
        dataIndex: 'shortName',
        width: 250,
        render: this.renderShortNameCell,
      },
      {
        title: Lang.t('department.fullName'),
        dataIndex: 'name',
        width: 150,
        render: (text: any) => <span className="dept-name-cell">{text}</span>,
      },
      {
        title: Lang.t('department.type'),
        dataIndex: 'type',
        width: 100,
        render: (text: any, record: any) => (
          <Tag color={that.getDeptTypeColor(record.type)} className="dept-type-tag">
            {that.getDeptTypeText(record.type)}
          </Tag>
        ),
      },

      {
        title: Lang.t('department.parentDept'),
        key: 'parentId',
        width: 120,
        render: (text: any, record: any) => {
          if (!record.parentId || record.parentId === 0) {
            return <span className="parent-name-cell">--</span>;
          }
          
          // 在平铺数据中查找父部门
          const parentDept = flatData.find(dept => dept.id === record.parentId);
          return (
            <span className="parent-name-cell">
              {parentDept ? (parentDept.shortName || parentDept.name) : `ID: ${record.parentId}`}
            </span>
          );
        },
      },
      {
        title: Lang.t('department.status'),
        dataIndex: 'status',
        width: 80,
        render: (text: any, record: any) => {
          const status = record.status || '0';
          return (
            <Tag color={that.getStatusColor(status)} className="status-tag">
              {that.getStatusText(status)}
            </Tag>
          );
        },
      },
      {
        title: Lang.t('createTime'),
        dataIndex: 'createTime',
        width: 160,
        render: (text: any) => {
          // 处理 Date 类型或字符串类型的日期
          if (text instanceof Date) {
            return DateUtil.simpleDateTime(text);
          }
          return text ? DateUtil.simpleDateTime(text) : '--';
        },
      },
      {
        title: Lang.t('operation'),
        dataIndex: 'action',
        width: 180,
        fixed: 'right' as const,
        render: this.renderActionCell,
      },
    ];

    const rowSelection = {
      selectedRowKeys: selectedRowKeys as React.Key[],
      onChange: this.handleSelectChange,
      getCheckboxProps: (record: any) => ({
        // 使用 id 作为选择标识
        name: record.id ? record.id.toString() : '',
      }),
      selections: [
        Table.SELECTION_ALL,
        Table.SELECTION_INVERT,
        Table.SELECTION_NONE,
      ],
    };

    // 配置展开属性
    const expandableConfig = {
      expandedRowKeys,
      onExpand: (expanded: boolean, record: any) => this.toggleExpand(expanded, record),
      childrenColumnName: 'children',
      rowExpandable: (record: any) => record.children && record.children.length > 0,
    };

    return (
      <div className="page-department-list">
        <HwzTitle name={Lang.t('layout.departments')}>
          {this.renderTableToolbar()}
        </HwzTitle>

        {/* 查询条件面板 */}
        {this.renderSearchForm()}

        {/* 部门列表表格 */}
        <Table
          rowKey={(record) => record.id ? record.id.toString() : ''}
          loading={loading}
          dataSource={treeData}
          columns={columns}
          expandable={expandableConfig}
          rowSelection={rowSelection}
          className="department-tree-table"
          locale={{
            emptyText: (
              <div style={{ padding: '40px 0', textAlign: 'center' }}>
                <div style={{ fontSize: '16px', color: '#999', marginBottom: '8px' }}>
                  {Lang.t('noData')}
                </div>
                <Button 
                  type="primary" 
                  onClick={this.refresh}
                >
                  {Lang.t('refresh')}
                </Button>
              </div>
            ),
          }}
          scroll={{ x: 1200 }}
          size="middle"
          pagination={{
            total: flatData.length,
            pageSize: hasSearchCondition ? 20 : 100, // 有搜索条件时分页，否则显示全部
            showSizeChanger: hasSearchCondition,
            showQuickJumper: hasSearchCondition,
            showTotal: (total: number, range: [number, number]) => {
              return `${Lang.t('total')} ${total} ${Lang.t('items')}`;
            },
            hideOnSinglePage: !hasSearchCondition // 只有一页时隐藏分页器（树形结构时）
          }}
        />

        {/* 部门弹窗 */}
        {modalVisible && currentDept && (
          <DeptModal
            visible={modalVisible}
            type={modalType}
            dept={currentDept}
            allDepts={flatData}
            onCancel={this.closeModal}
            onSuccess={this.handleModalSuccess}
          />
        )}

        {/* 导入对话框 */}
        {this.renderImportDialog()}
      </div>
    );
  }
}